import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A reusable hook for dropdown components.
 * Manages open state, positioning, and click-outside handling.
 *
 * @param {Object} options - Configuration options
 * @param {number} [options.gap=4] - Vertical gap between button and dropdown in pixels
 * @param {number} [options.attachDelay=0] - Delay before attaching click-outside listener (prevents immediate close)
 * @returns {Object} Dropdown state and handlers
 */

interface Props {
  gap?: number;
  attachDelay?: number;
}

const useDropdown = ({ gap = 4, attachDelay = 0 }: Props = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ x: 0, y: 0 });

  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);

  // Click-outside handler
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !(wrapperRef.current as HTMLElement).contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    // Optionally delay listener attachment to prevent immediate close
    if (attachDelay > 0) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, attachDelay);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, attachDelay]);

  // Toggle dropdown and calculate position
  const toggle = useCallback(
    (e: MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!isOpen && buttonRef.current) {
        const rect = (buttonRef.current as HTMLElement).getBoundingClientRect();
        setDropdownPos({ x: rect.left, y: rect.bottom + gap });
      }

      setIsOpen((prev) => !prev);
    },
    [isOpen, gap]
  );

  // Open dropdown and calculate position
  const open = useCallback(
    (e: MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (buttonRef.current) {
        const rect = (buttonRef.current as HTMLElement).getBoundingClientRect();
        setDropdownPos({ x: rect.left, y: rect.bottom + gap });
      }

      setIsOpen(true);
    },
    [gap]
  );

  // Close dropdown
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    dropdownPos,
    wrapperRef,
    buttonRef,
    toggle,
    open,
    close,
    setDropdownPos,
  };
};

export default useDropdown;
